import { useEffect, useRef, useState } from 'react';
import MediaStreamRecorder from 'msr';
import { getMicrophonePermission } from '@src/services/microphone';
import {
  decodeWAV,
  exportAudioBufferToAudio,
  toBuffer,
} from '@src/services/audio';
import {
  MIN_TIME_PER_SENTENCE,
  RECORD_INTERVAL,
  TIME_PER_SYLLABLE,
} from '@src/constants/voiceCloning';
import { countSyllables } from '@src/utils/string';

const useRecording = () => {
  const recorder = useRef(null);
  const permissionUseMicRef = useRef(null);
  const startTime = useRef(null);
  const audioBuffer = useRef(Buffer.from([]));
  const stream = useRef(null);
  const audioBlobs = useRef([]);
  const recordingTimeoutRef = useRef(null);
  const [textToRecord, setTextToRecord] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [interruptedAudioDuration, setInterruptedAudioDuration] = useState(0);
  const [interruptedAudioLink, setInterruptedAudioLink] = useState(null);
  const [isVCNewRecording, setIsVCNewRecording] = useState(false);

  const setContentRecord = (text) => setTextToRecord(text);
  const setRecordingMethod = (method) => setIsVCNewRecording(method);

  const calculateRecordTime = (sentence) => {
    const numberOfSyllables = countSyllables(sentence);
    let recordTime = numberOfSyllables * TIME_PER_SYLLABLE;

    if (recordTime < MIN_TIME_PER_SENTENCE) recordTime = MIN_TIME_PER_SENTENCE;

    return recordTime;
  };

  const stopRecording = async () => {
    if (!recorder.current) return { recordTime: 0, audioLink: null };
    // Clear the timeout
    clearTimeout(recordingTimeoutRef.current);
    clearInterval(permissionUseMicRef.current);

    // get time stop record
    const endTime = Date.now();

    // calculate record time
    const recordTime = endTime - startTime.current;

    // close stream
    if (recorder.current && stream.current) {
      recorder.current.stop();
      const tracks = stream.current.getTracks();
      tracks.forEach((track) => track.stop());
    }

    recorder.current = null;
    stream.current = null;

    const audioLink = await exportAudioBufferToAudio(audioBuffer.current);
    stream.current = null;
    recorder.current = null;
    audioBlobs.current = [];
    audioBuffer.current = Buffer.from([]);
    setIsRecording(false);

    return { recordTime, audioLink };
  };

  const startRecording = async () => {
    if (recorder.current || isRecording) return;

    permissionUseMicRef.current = setInterval(async () => {
      const permissionUseMic = await getMicrophonePermission();
      if (!permissionUseMic) {
        const { recordTime, audioLink } = await stopRecording();
        setInterruptedAudioDuration(recordTime);
        setInterruptedAudioLink(audioLink);
        setIsRecording(false);
      }
    }, 1000);

    // get time start record
    startTime.current = Date.now();

    // buffer
    audioBlobs.current = [];
    audioBuffer.current = Buffer.from([]);

    // audio stream
    stream.current = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false,
    });

    recorder.current = new MediaStreamRecorder(stream.current);
    recorder.current.mimeType = 'audio/wav';
    recorder.current.audioChannels = 1;
    recorder.current.ondataavailable = async (e) => {
      const blobData = e;

      // blobs
      audioBlobs.current.push(blobData);

      // array buffer
      const arrayBufferData = await blobData.arrayBuffer();

      // buffer
      const bufferData = toBuffer(arrayBufferData);
      const decodedWav = decodeWAV(bufferData);
      const headlessBufferData = Buffer.from(decodedWav);
      audioBuffer.current = Buffer.concat([
        audioBuffer.current,
        headlessBufferData,
      ]);
    };
    recorder.current.start(RECORD_INTERVAL);
    setIsRecording(true);

    // Stop recording after the calculated time has passed
    if (textToRecord) {
      const recordTimeAllowed = calculateRecordTime(textToRecord);
      recordingTimeoutRef.current = setTimeout(async () => {
        const { recordTime, audioLink } = await stopRecording();
        setInterruptedAudioDuration(recordTime);
        setInterruptedAudioLink(audioLink);
        setIsRecording(false);
      }, recordTimeAllowed);
    }
  };

  const newStopRecording = async () => {
    if (!recorder.current) return { recordTime: 0, audioLink: null };
    // Clear the timeout
    clearTimeout(recordingTimeoutRef.current);
    clearInterval(permissionUseMicRef.current);

    // get time stop record
    const endTime = Date.now();

    // calculate record time
    const recordTime = endTime - startTime.current;

    // close stream
    if (recorder.current && stream.current) {
      recorder.current.stop();
      const tracks = stream.current.getTracks();
      tracks.forEach((track) => track.stop());
    }

    await Promise.all(
      audioBlobs.current.map(async (blobData) => {
        // array buffer
        const arrayBufferData = await blobData.arrayBuffer();

        // buffer
        const bufferData = toBuffer(arrayBufferData);
        const decodedWav = decodeWAV(bufferData);
        const headlessBufferData = Buffer.from(decodedWav);
        audioBuffer.current = Buffer.concat([
          audioBuffer.current,
          headlessBufferData,
        ]);
      }),
    );

    recorder.current = null;
    stream.current = null;

    const audioLink = await exportAudioBufferToAudio(audioBuffer.current);
    stream.current = null;
    recorder.current = null;
    audioBlobs.current = [];
    audioBuffer.current = Buffer.from([]);
    setIsRecording(false);

    return { recordTime, audioLink };
  };

  const newStartRecording = async () => {
    if (recorder.current || isRecording) return;

    permissionUseMicRef.current = setInterval(async () => {
      const permissionUseMic = await getMicrophonePermission();
      if (!permissionUseMic) {
        const { recordTime, audioLink } = await newStopRecording();
        setInterruptedAudioDuration(recordTime);
        setInterruptedAudioLink(audioLink);
        setIsRecording(false);
      }
    }, 1000);

    // get time start record
    startTime.current = Date.now();

    // buffer
    audioBlobs.current = [];
    audioBuffer.current = Buffer.from([]);

    // audio stream
    stream.current = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
        sampleRate: 48000,
        sampleSize: 16,
      },
      video: false,
    });

    recorder.current = new MediaStreamRecorder(stream.current);
    recorder.current.mimeType = 'audio/wav';
    recorder.current.audioChannels = 1;
    recorder.current.sampleRate = 48000;
    recorder.current.audioBitsPerSecond = 16;
    recorder.current.ondataavailable = async (e) => {
      const blobData = e;

      // blobs
      audioBlobs.current.push(blobData);
    };
    recorder.current.start(RECORD_INTERVAL);

    setTimeout(() => {
      setIsRecording(true);
    }, 100);

    // Stop recording after the calculated time has passed
    if (textToRecord) {
      const recordTimeAllowed = calculateRecordTime(textToRecord);
      recordingTimeoutRef.current = setTimeout(async () => {
        const { recordTime, audioLink } = await newStopRecording();
        setInterruptedAudioDuration(recordTime);
        setInterruptedAudioLink(audioLink);
        setIsRecording(false);
      }, recordTimeAllowed);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = async () => {
      const { recordTime, audioLink } = isVCNewRecording
        ? await newStopRecording()
        : await stopRecording(); // Stop recording if the user is leaving
      setInterruptedAudioDuration(recordTime);
      setInterruptedAudioLink(audioLink);
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden') {
        const { recordTime, audioLink } = isVCNewRecording
          ? await newStopRecording()
          : await stopRecording(); // Stop recording if the user switches tabs or minimizes the browser
        setInterruptedAudioDuration(recordTime);
        setInterruptedAudioLink(audioLink);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isRecording, stopRecording]);

  return {
    isRecording,
    interruptedAudioLink,
    interruptedAudioDuration,
    startRecording,
    stopRecording,
    setContentRecord,
    newStartRecording,
    newStopRecording,
    setRecordingMethod,
  };
};

export default useRecording;
